/*
WARNING: unreadable code ahead! This was written at 2AM so I wasn't really focusing on making this readable. For a true parser, see pdf-lib.
*/
import * as forge from "node-forge";

enum CharCodes {
  Null = 0,
  Backspace = 8,
  Tab = 9,
  Newline = 10,
  FormFeed = 12,
  CarriageReturn = 13,
  Space = 32,
  ExclamationPoint = 33,
  Hash = 35,
  Percent = 37,
  LeftParen = 40,
  RightParen = 41,
  Plus = 43,
  Minus = 45,
  Dash = 45,
  Period = 46,
  ForwardSlash = 47,
  Zero = 48,
  One = 49,
  Two = 50,
  Three = 51,
  Four = 52,
  Five = 53,
  Six = 54,
  Seven = 55,
  Eight = 56,
  Nine = 57,
  LessThan = 60,
  GreaterThan = 62,
  A = 65,
  D = 68,
  E = 69,
  F = 70,
  O = 79,
  P = 80,
  R = 82,
  LeftSquareBracket = 91,
  BackSlash = 92,
  RightSquareBracket = 93,
  a = 97,
  b = 98,
  d = 100,
  e = 101,
  f = 102,
  i = 105,
  j = 106,
  l = 108,
  m = 109,
  n = 110,
  o = 111,
  r = 114,
  s = 115,
  t = 116,
  u = 117,
  x = 120,
  LeftCurly = 123,
  RightCurly = 125,
  Tilde = 126,
}

const IsWhitespace = new Uint8Array(256);
const IsDigit = new Uint8Array(256);
const IsDelimiter = new Uint8Array(256);

IsDelimiter[CharCodes.LeftParen] = 1;
IsDelimiter[CharCodes.RightParen] = 1;
IsDelimiter[CharCodes.LessThan] = 1;
IsDelimiter[CharCodes.GreaterThan] = 1;
IsDelimiter[CharCodes.LeftSquareBracket] = 1;
IsDelimiter[CharCodes.RightSquareBracket] = 1;
//IsDelimiter[CharCodes.LeftCurly] = 1;
//IsDelimiter[CharCodes.RightCurly] = 1;
IsDelimiter[CharCodes.ForwardSlash] = 1;
IsDelimiter[CharCodes.Percent] = 1;
IsDigit[CharCodes.Zero] = 1;
IsDigit[CharCodes.One] = 1;
IsDigit[CharCodes.Two] = 1;
IsDigit[CharCodes.Three] = 1;
IsDigit[CharCodes.Four] = 1;
IsDigit[CharCodes.Five] = 1;
IsDigit[CharCodes.Six] = 1;
IsDigit[CharCodes.Seven] = 1;
IsDigit[CharCodes.Eight] = 1;
IsDigit[CharCodes.Nine] = 1;
IsWhitespace[CharCodes.Null] = 1;
IsWhitespace[CharCodes.Tab] = 1;
IsWhitespace[CharCodes.Newline] = 1;
IsWhitespace[CharCodes.FormFeed] = 1;
IsWhitespace[CharCodes.CarriageReturn] = 1;
IsWhitespace[CharCodes.Space] = 1;

const getAsciiCode = (char: string) => char.charCodeAt(0);
const CONTENTS = Uint8Array.from("/Contents", getAsciiCode);
const BYTERANGE = Uint8Array.from("/ByteRange", getAsciiCode);
const M = Uint8Array.from("/M", getAsciiCode);
const TYPE = Uint8Array.from("/Type", getAsciiCode);
const SIG = Uint8Array.from("/Sig", getAsciiCode);
const SUBFILTER = Uint8Array.from("/SubFilter", getAsciiCode);
const ADBE_PKCS7_DETACHED = Uint8Array.from("/adbe.pkcs7.detached", getAsciiCode);
const STREAM = Uint8Array.from("stream", getAsciiCode);
const END_STREAM = Uint8Array.from("endstream", getAsciiCode);
const LENGTH = Uint8Array.from("/Length", getAsciiCode);

export type Asn1 = forge.asn1.Asn1;
export type CMS = forge.pkcs7.Captured<forge.pkcs7.PkcsSignedData>;
export type Signature = {
  rawCms: string;
  signedInterval: Buffer;
  M: number | undefined;
  byteRange: [number, number, number, number];
};
export class PDFParser {
  /*
    Manual parsing because pdf-lib is way too slow for this.
    Basically, we try to find a top level dictionary with the following entries:
        - `/Contents` (required)
        - `/ByteRange` (required)
        - `/Type /Sig` (optional)
        - `/SubFilter /adbe.pkcs7.detached` (optional)

    Those are explained in section 12.8 of the 2.0 PDF ISO.
    To make it easier to code, we also assume that any signature dictionary will be a top-level dictionary.
    */
  static parseDocument(document: Uint8Array): Signature[] {
    let position = 0;

    const signatures: Signature[] = [];
    const buffer = new Uint8Array(1024);
    while (position < document.length) {
      let sig: Signature | null;
      [position, sig] = PDFParser.findAndParseSignature(document, position, buffer);

      if (sig) signatures.push(sig);
    }
    return signatures;
  }

  private static findAndParseSignature(document: Uint8Array, offset: number, buffer: Uint8Array): [number, Signature | null] {
    while (offset < document.length) {
      while (offset < document.length - 1 && (document[offset] !== CharCodes.LessThan || document[offset + 1] !== CharCodes.LessThan))
        offset++;

      if (offset === document.length) return [offset, null];

      let contents: string | undefined = undefined;
      let m: number | undefined = undefined;
      let byterange: [number, number, number, number] | undefined = undefined;

      offset += 2;
      const ogOffset = offset;
      let typeSig: boolean | undefined = undefined;
      let correctSubfilter: boolean | undefined = undefined;

      let bufferSize = 0;
      while (offset < document.length) {
        while (!IsDelimiter[document[offset]]) offset++;

        if (document[offset] === CharCodes.GreaterThan && document[offset + 1] === CharCodes.GreaterThan) break;

        if (document[offset] !== CharCodes.ForwardSlash) throw Error("Char should be ForwardSlash!");
        bufferSize = 0;
        //let cur = "";
        do {
          buffer[bufferSize++] = document[offset++];
          //cur += String.fromCharCode(document[offset - 1]);
        } while (!IsDelimiter[document[offset]] && !IsWhitespace[document[offset]]);
        //jsconsole.log(cur);

        // Found an entry. Let's see if it's /ByteRange or /Type /Sig
        if (PDFParser.cmpArray(buffer, bufferSize, TYPE, TYPE.length)) {
          // Found /Type! Now let's see if it's /Sig

          [typeSig, offset] = PDFParser.parseNameAndMatch(document, offset, SIG);
          continue;
        } else if (PDFParser.cmpArray(buffer, bufferSize, BYTERANGE, BYTERANGE.length)) {
          // Found /ByteRange entry.
          offset = PDFParser.skipWhitespaceAndComments(document, offset);

          if (document[offset] === CharCodes.LeftSquareBracket) {
            byterange = [0, 0, 0, 0];
            offset++;
            for (let i = 0; i < 4; ++i) {
              offset = PDFParser.skipWhitespaceAndComments(document, offset);

              let cur = "";
              while (IsDigit[document[offset]]) {
                cur += String.fromCharCode(document[offset]);
                offset++;
              }

              byterange[i] = parseInt(cur);
            }

            while (document[offset] !== CharCodes.RightSquareBracket) offset++;
            offset++;
          } else {
            offset = PDFParser.skipObject(document, offset, "/");
          }
        } else if (PDFParser.cmpArray(buffer, bufferSize, CONTENTS, CONTENTS.length)) {
          // Found /Contents
          offset = PDFParser.skipWhitespaceAndComments(document, offset);

          if (document[offset] === CharCodes.LessThan && document[offset + 1] !== CharCodes.LessThan) {
            offset++;
            contents = "";
            while (document[offset] !== CharCodes.GreaterThan) {
              contents += String.fromCharCode(document[offset]);
              offset++;
            }
            offset++;
          } else {
            offset = PDFParser.skipObject(document, offset, "/");
          }
        } else if (PDFParser.cmpArray(buffer, bufferSize, M, M.length)) {
          offset = PDFParser.skipWhitespaceAndComments(document, offset);
          let strDate = "";

          if (document[offset] === CharCodes.LeftParen) {
            offset++;
            while (document[offset] !== CharCodes.RightParen) {
              strDate += String.fromCharCode(document[offset]);
              offset++;
            }
            m = PDFParser.parseDate(strDate);
            offset++;
          } else {
            offset = PDFParser.skipObject(document, offset, "/");
          }
        } else if (PDFParser.cmpArray(buffer, bufferSize, SUBFILTER, SUBFILTER.length)) {
          [correctSubfilter, offset] = PDFParser.parseNameAndMatch(document, offset, ADBE_PKCS7_DETACHED);
        } else {
          offset = PDFParser.skipObject(document, offset, "/");
        }
      }

      if (typeSig !== false && correctSubfilter !== false && contents !== undefined && byterange !== undefined) {
        return [
          offset,
          {
            rawCms: contents,
            M: m,
            signedInterval: Buffer.concat([
              document.subarray(byterange[0], byterange[0] + byterange[1]),
              document.subarray(byterange[2], byterange[2] + byterange[3]),
            ]),
            byteRange: byterange,
          },
        ];
      }

      // We're out of the dictionary, but it can be a stream.
      offset += 2;
      offset = PDFParser.skipWhitespaceAndComments(document, offset);
      if (PDFParser.cmpArray(document.subarray(offset, offset + STREAM.length), STREAM.length, STREAM, STREAM.length))
        offset = PDFParser.skipStream(document, ogOffset, offset);
    }
    return [offset, null];
  }

  private static parseNameAndMatch(document: Uint8Array, offset: number, expected: Uint8Array): [boolean, number] {
    offset = PDFParser.skipWhitespaceAndComments(document, offset);

    if (offset === document.length) return [false, offset];

    // If it's not a /, object won't be /Sig
    if (document[offset] !== CharCodes.ForwardSlash) {
      return [false, PDFParser.skipObject(document, offset, "/")];
    }
    offset++;
    const begin = offset;
    while (offset < document.length && offset - begin < expected.length) {
      if (document[offset] !== expected[offset - begin]) break;
      offset++;
    }

    const sigFound = offset - begin < expected.length;

    while (offset < document.length && !IsDelimiter[document[offset]]) offset++;

    return [sigFound, offset];
  }

  private static skipObject(document: Uint8Array, offset: number, delim: string | undefined = undefined): number {
    offset = PDFParser.skipWhitespaceAndComments(document, offset);

    if (document[offset] === CharCodes.LeftParen) {
      // string
      let depth = 1;
      offset++;

      let escaped = false;
      for (; offset < document.length && depth > 0; ++offset) {
        const char = document[offset];
        if (char === CharCodes.BackSlash) escaped = !escaped;
        else if (!escaped && char === CharCodes.LeftParen) depth++;
        else if (!escaped && char === CharCodes.RightParen) depth--;
        else if (escaped) escaped = false;
      }
    } else if (document[offset] === CharCodes.ForwardSlash) {
      offset++;
      while (!IsDelimiter[document[offset]]) offset++;
    } else if (document[offset] === CharCodes.LeftSquareBracket) {
      let depth = 1;
      offset++;
      while (offset < document.length && depth > 0) {
        offset = PDFParser.skipWhitespaceAndComments(document, offset);
        offset = this.skipObject(document, offset);

        if (document[offset] === CharCodes.RightSquareBracket) depth--, offset++;
      }
    } else if (document[offset] === CharCodes.LessThan) {
      offset++;
      if (document[offset] !== CharCodes.LessThan) {
        while (document[offset] !== CharCodes.GreaterThan) offset++;
        offset++;
      } else {
        offset++;

        const dictBegin = offset;
        while (offset < document.length) {
          offset = PDFParser.skipWhitespaceAndComments(document, offset);

          // skip entry
          //offset = PDFParser.skipObject(document, offset);
          if (document[offset] === CharCodes.ForwardSlash) {
            offset++;
            while (!IsDelimiter[document[offset]] && !IsWhitespace[document[offset]]) offset++;
          }

          if (offset >= document.length) return offset;

          // skip value
          offset = PDFParser.skipObject(document, offset, "/");
          if (offset >= document.length) return offset;

          if (document[offset] === CharCodes.GreaterThan && document[offset + 1] === CharCodes.GreaterThan) {
            offset += 2;

            if (!PDFParser.cmpArray(document.subarray(offset, offset + STREAM.length), STREAM.length, STREAM, STREAM.length)) {
              break;
            }

            // Well...we're inside a stream.
            // Since we don't really want to walk through the whole thing, let's try to retrieve /Length
            let curOffset = dictBegin;
            offset = PDFParser.skipStream(document, curOffset, offset);
            break;
          }
        }
      }
    }

    // Just skip until delim

    while (!IsDelimiter[document[offset]]) offset++;
    return offset;
  }

  private static skipStream(document: Uint8Array, curOffset: number, offset: number): number {
    offset += STREAM.length;
    while (document[offset] === CharCodes.CarriageReturn || document[offset] === CharCodes.Newline) offset++;
    let length = -1;
    while (curOffset < offset) {
      curOffset = PDFParser.skipWhitespaceAndComments(document, curOffset);

      if (PDFParser.cmpArray(document.subarray(curOffset, curOffset + LENGTH.length), LENGTH.length, LENGTH, LENGTH.length)) {
        curOffset = curOffset + LENGTH.length;
        curOffset = PDFParser.skipWhitespaceAndComments(document, curOffset);

        let numberStr = "";
        while (IsDigit[document[curOffset]]) {
          numberStr += String.fromCharCode(document[curOffset]);
          curOffset++;
        }
        length = parseInt(numberStr);
        break;
      }

      //curOffset = PDFParser.skipObject(document, curOffset);
      if (document[curOffset] === CharCodes.ForwardSlash) {
        curOffset++;
        while (!IsDelimiter[document[curOffset]] && !IsWhitespace[document[curOffset]]) curOffset++;
      }
      curOffset = PDFParser.skipObject(document, curOffset, "/");
    }

    if (length !== -1) {
      let end = offset + length;
      end = PDFParser.skipWhitespaceAndComments(document, end);

      if (PDFParser.cmpArray(document.subarray(end, end + END_STREAM.length), END_STREAM.length, END_STREAM, END_STREAM.length)) {
        offset = end + END_STREAM.length + 1;
        while (!IsDelimiter[document[offset]]) offset++;
        return offset;
      }
      end++;
      if (PDFParser.cmpArray(document.subarray(end, end + END_STREAM.length), END_STREAM.length, END_STREAM, END_STREAM.length)) {
        offset = end + END_STREAM.length + 1;
        while (!IsDelimiter[document[offset]]) offset++;
        return offset;
      }
      end++;
      if (PDFParser.cmpArray(document.subarray(end, end + END_STREAM.length), END_STREAM.length, END_STREAM, END_STREAM.length)) {
        offset = end + END_STREAM.length + 1;
        while (!IsDelimiter[document[offset]]) offset++;
        return offset;
      }
    }

    let idx = 0,
      endStreamIdx = 0,
      depth = 1;
    while (depth > 0 && offset < document.length) {
      const char = document[offset];

      if (char === STREAM[idx]) idx++;
      else idx = char === STREAM[0] ? 1 : 0;
      if (char === END_STREAM[endStreamIdx]) endStreamIdx++;
      else endStreamIdx = char === END_STREAM[0] ? 1 : 0;

      if (idx === STREAM.length && endStreamIdx !== END_STREAM.length) {
        depth++;
        idx = 0;
      }
      if (endStreamIdx === END_STREAM.length) {
        depth--;
        endStreamIdx = 0;
      }
      offset++;
    }
    while (!IsDelimiter[document[offset]]) offset++;
    return offset;
  }

  private static skipWhitespaceAndComments(document: Uint8Array, offset: number): number {
    while (offset < document.length) {
      if (document[offset] === CharCodes.Percent) {
        offset++;
        while (offset < document.length && document[offset] !== CharCodes.Newline && document[offset] !== CharCodes.CarriageReturn)
          offset++;
      } else if (IsWhitespace[document[offset]]) {
        offset++;
      } else {
        break;
      }
    }
    return offset;
  }

  private static cmpArray(a: Uint8Array, a_size: number, b: Uint8Array, b_size: number): boolean {
    if (a_size !== b_size) return false;
    for (let i = 0; i < a_size; ++i) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }

  private static parseDate(date: string): number {
    const dateStr = date.replace(/\\(\d{3})/g, (_, octal) => String.fromCharCode(parseInt(octal, 8)));

    const year = dateStr.slice(2, 6);
    const month = dateStr.slice(6, 8) || "1";
    const day = dateStr.slice(8, 10) || "1";

    const hour = dateStr.slice(10, 12) || "0";
    const minute = dateStr.slice(12, 14) || "0";
    const second = dateStr.slice(14, 16) || "0";

    const signal = dateStr.slice(16, 17) || "Z";
    const hourTz = dateStr.slice(17, 19) || "0";
    const minuteTz = dateStr.slice(20, 22) || "0";

    const formattedDate = `${year}-${month}-${day}T${hour}:${minute}:${second}${signal}${signal != "Z" ? `${hourTz}:${minuteTz}` : ""}`;
    return new Date(formattedDate).getTime();
  }

  static getMessageFromSignature(signature: string): [Asn1, CMS] {
    while (signature[signature.length - 1] === "0" && signature[signature.length - 2] === "0")
      signature = signature.substring(0, signature.length - 2);
    const p7Asn1 = forge.asn1.fromDer(Buffer.from(signature, "hex").toString("latin1"), false);
    return [p7Asn1, forge.pkcs7.messageFromAsn1(p7Asn1) as CMS];
  }
}
